// ── Shared deadline parsing helpers (mirrors Programs.jsx logic) ──

const NO_DEADLINE_PATTERNS = [
  'no deadline',
  'deadline not specified',
  'no application deadline',
  'no fixed deadline',
  'registration not required',
  'no registration required',
  'until the event day',
  'until event day',
  'until meeting day',
  'վերջնաժամկետ չկա',
  'վերջնաժամկետ նշված չէ',
  'գրանցում չի պահանջվում',
  'մինչև հանդիպման օրը',
  'մինչեւ հանդիպման օրը',
];

export function hasNoDeadlineSignal(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return false;
  return NO_DEADLINE_PATTERNS.some((pattern) => text.includes(pattern));
}

export function extractGenericDateCandidate(text) {
  if (!text) return '';
  const raw = String(text);
  const longDate = raw.match(/\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|sept|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+\d{1,2},?\s+20\d{2}\b/i);
  if (longDate) return longDate[0];
  const numeric = raw.match(/\b\d{1,2}[\/.-]\d{1,2}[\/.-](?:20)?\d{2}\b/);
  if (numeric) return numeric[0];
  return '';
}

export function extractDateCandidateFromText(text) {
  if (!text) return '';
  const raw = String(text);
  const lower = raw.toLowerCase();
  const markers = [
    'deadline',
    'application deadline',
    'apply by',
    'closing date',
    'registration deadline',
    'գրանցման վերջնաժամկետ',
    'վերջնաժամկետ',
    'դիմումների վերջնաժամկետ',
  ];
  for (const marker of markers) {
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const tail = raw.slice(idx, idx + 120);
      const line = tail.split('\n')[0] || tail;
      const afterMarker = line.slice(marker.length);
      const after = afterMarker.replace(/^[\s՚՛՝ʼ‘’`'`：:：\-–—]+/, '').trim();
      if (after) return after;
    }
  }
  return extractGenericDateCandidate(raw);
}

// ── Parse deadline string into a Date object ──
// Handles: ISO, Armenian month names + "ժամը՝ HH:MM" / "HH։MM" / "HH:MM"
export function parseDeadlineDate(value) {
  const str = String(value || '').trim();
  if (!str) return null;

  const timeMatch = str.match(/(?:ժամ[ըը]?(?:[՝:]\s*|\s+))?([01]?\d|2[0-3])\s*[։:]\s*([0-5]\d)\b/i);
  const hh = timeMatch ? parseInt(timeMatch[1], 10) : null;
  const mm = timeMatch ? parseInt(timeMatch[2], 10) : null;
  const hasTime = hh !== null && mm !== null;

  const normalized = str
    .replace(/[։]/g, ':')
    .replace(/ժամ[ըը]?(?:[՝:]\s*|\s+)\d{1,2}:\d{2}/gi, '')
    .replace(/ժամ[ըը]?\s*/gi, '')
    .replace(/\bթ\.?\b/gi, '')
    .replace(/[՝՛]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const direct = new Date(normalized);
  if (!isNaN(direct.getTime()) && direct.getFullYear() > 2000) {
    if (hasTime) direct.setHours(hh, mm, 0, 0);
    return direct;
  }

  const amMonths = {
    հունվարի:0, փետրվարի:1, մարտի:2, ապրիլի:3, մայիսի:4, հունիսի:5,
    հուլիսի:6, օգոստոսի:7, սեպտեմբերի:8, հոկտեմբերի:9, նոյեմբերի:10, դեկտեմբերի:11,
  };
  const lower = normalized.toLowerCase();
  const yearM = lower.match(/\b(20\d{2})\b/);
  const noYear = lower.replace(/20\d{2}/, '####');
  const dayM = noYear.match(/\b([0-2]?\d|3[01])\b/);
  let month = null;
  for (const [name, idx] of Object.entries(amMonths)) {
    if (lower.includes(name)) { month = idx; break; }
  }
  if (month !== null && dayM) {
    const year = yearM ? parseInt(yearM[1], 10) : new Date().getFullYear();
    const d = new Date(year, month, parseInt(dayM[1], 10), hasTime ? hh : 23, hasTime ? mm : 59, 0);
    if (!isNaN(d.getTime())) return d;
  }

  const enMonths = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11,
    january:0,february:1,march:2,april:3,june:5,july:6,august:7,september:8,october:9,november:10,december:11 };
  for (const [name, idx] of Object.entries(enMonths)) {
    if (lower.includes(name)) {
      const yearE = lower.match(/\b(20\d{2})\b/);
      const dayE = lower.replace(name, '').match(/\b(\d{1,2})\b/);
      if (dayE) {
        const year = yearE ? parseInt(yearE[1]) : new Date().getFullYear();
        const d = new Date(year, idx, parseInt(dayE[1], 10), hasTime ? hh : 23, hasTime ? mm : 59, 0);
        if (!isNaN(d.getTime())) return d;
      }
    }
  }
  return null;
}

// Resolve the best deadline date for a program (deadline field, else dates found in text).
export function getProgramDeadlineDate(program) {
  const explicitDeadline = String(program?.deadline || '').trim();
  const text = `${program?.title || ''} ${program?.description || ''} ${program?.full_text || ''}`;
  if (hasNoDeadlineSignal(explicitDeadline) || hasNoDeadlineSignal(text)) return null;

  const value = explicitDeadline || extractDateCandidateFromText(text);
  if (!value) return null;
  return parseDeadlineDate(value);
}

function isSameCalendarDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Is the program's deadline today or tomorrow (calendar day, not just next 24h)?
export function isDeadlineTodayOrTomorrow(program) {
  const date = getProgramDeadlineDate(program);
  if (!date) return false;
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  return isSameCalendarDay(date, now) || isSameCalendarDay(date, tomorrow);
}

// Pick programs to show in the "Deadline Soon" rail: prefer today/tomorrow,
// but always try to fill up to `minCount` cards by widening the window
// (up to 3 days, then to whatever upcoming deadlines exist) so the section
// isn't left empty or half-full on a quiet day.
export function pickDeadlineSoonPrograms(list, minCount = 4, maxCount = 6) {
  const now = Date.now();
  const withDeadline = (Array.isArray(list) ? list : [])
    .map(program => ({ program, date: getProgramDeadlineDate(program) }))
    .filter(x => x.date && x.date.getTime() > now)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const within = (days) => withDeadline.filter(x => x.date.getTime() - now <= days * 24 * 60 * 60 * 1000);

  let picked = within(2); // today/tomorrow
  if (picked.length < minCount) picked = within(3);
  if (picked.length < minCount) picked = withDeadline; // still short — just take the soonest overall

  return picked.slice(0, maxCount).map(x => ({ program: x.program, deadline: x.date }));
}

// Live-friendly breakdown of the time remaining until a deadline Date.
export function getCountdownParts(deadline) {
  const diff = Math.max(0, deadline.getTime() - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, expired: diff <= 0 };
}

// Compact "Aug 20" style label for tight card layouts.
export function formatDeadlineDateShort(date, locale) {
  try {
    return new Intl.DateTimeFormat(locale || undefined, { day: 'numeric', month: 'short' }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

export function formatDeadlineDate(date, locale) {
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}
